import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * InstagramFeed — site sahibinin seçtiği Instagram gönderilerini verilen
 * sırayla gösteren yatay carousel.
 */

export interface CuratedInstagramPost {
  id: string;
  permalink: string;
  image: string;
  isCarousel?: boolean;
}

export const CURATED_POSTS: CuratedInstagramPost[] = [
  { id: 'DJQkzXIsV6n', permalink: 'https://www.instagram.com/p/DJQkzXIsV6n/', image: '/images/instagram/DJQkzXIsV6n.jpg' },
  { id: 'DV-trkojBJv', permalink: 'https://www.instagram.com/p/DV-trkojBJv/', image: '/images/instagram/DV-trkojBJv.jpg' },
  { id: 'DZutU52jI3_', permalink: 'https://www.instagram.com/p/DZutU52jI3_/', image: '/images/instagram/DZutU52jI3_.jpg' },
  { id: 'DVqfqVUDHtq', permalink: 'https://www.instagram.com/p/DVqfqVUDHtq/', image: '/images/instagram/DVqfqVUDHtq.jpg' },
  { id: 'DUDMtjmjFu4', permalink: 'https://www.instagram.com/p/DUDMtjmjFu4/?img_index=1', image: '/images/instagram/DUDMtjmjFu4.jpg', isCarousel: true },
  { id: 'DU0k4kGDDcK', permalink: 'https://www.instagram.com/p/DU0k4kGDDcK/', image: '/images/instagram/DU0k4kGDDcK.jpg' },
  { id: 'DUU7VfUjBoF', permalink: 'https://www.instagram.com/p/DUU7VfUjBoF/?img_index=1', image: '/images/instagram/DUU7VfUjBoF.jpg', isCarousel: true },
  { id: 'DT-OjfvjGXU', permalink: 'https://www.instagram.com/p/DT-OjfvjGXU/', image: '/images/instagram/DT-OjfvjGXU.jpg' },
];

interface InstagramFeedProps {
  title?: string;
  /** Görüntülenecek maksimum post sayısı (default 12) */
  limit?: number;
}

const InstagramFeed: React.FC<InstagramFeedProps> = ({
  title = "Bizi Instagram'da takip edin!",
  limit = 12,
}) => {
  const posts = CURATED_POSTS.slice(0, limit);

  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateButtons = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateButtons();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    return () => {
      el.removeEventListener('scroll', updateButtons);
      window.removeEventListener('resize', updateButtons);
    };
  }, [updateButtons, posts.length]);

  const scrollByStep = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('.instagram-card');
    const step = card ? card.offsetWidth + 20 : el.clientWidth * 0.8;
    el.scrollBy({ left: step * direction, behavior: 'smooth' });
  };

  return (
    <section className="instagram-feed-section">
      <h2 className="instagram-feed-title">{title}</h2>

      {posts.length > 0 && (
        <div className="instagram-carousel">
          <button
            type="button"
            className="instagram-arrow instagram-arrow-prev"
            onClick={() => scrollByStep(-1)}
            disabled={!canPrev}
            aria-label="Önceki gönderiler"
          >
            <svg width="22" height="38" viewBox="0 0 22 38" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 3L3 19L19 35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="instagram-grid" ref={trackRef}>
            {posts.map((post) => {
              const altText = 'Ayşegül Saraç Instagram gönderisi';
              return (
                <a
                  key={post.id}
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="instagram-card"
                  aria-label={altText}
                >
                  <div className="instagram-card-media">
                    <img
                      src={post.image}
                      alt={altText}
                      loading="lazy"
                      className="instagram-card-image"
                    />
                    {post.isCarousel && (
                      <span className="instagram-card-badge" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="7" y="7" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill="rgba(255,255,255,0.95)" />
                          <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill="rgba(255,255,255,0.95)" />
                        </svg>
                      </span>
                    )}
                    <span className="instagram-card-mark" aria-hidden="true">
                      <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.7" />
                        <circle cx="12" cy="12" r="4.25" stroke="currentColor" strokeWidth="1.7" />
                        <circle cx="17.4" cy="6.7" r="1.1" fill="currentColor" />
                      </svg>
                    </span>
                  </div>
                </a>
              );
            })}
          </div>

          <button
            type="button"
            className="instagram-arrow instagram-arrow-next"
            onClick={() => scrollByStep(1)}
            disabled={!canNext}
            aria-label="Sonraki gönderiler"
          >
            <svg width="22" height="38" viewBox="0 0 22 38" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3L19 19L3 35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
};

export default InstagramFeed;
